// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import XRegExp from 'xregexp'
import { coordsDD } from 'helpers/regex'
import { getCardinal } from 'helpers/coordsConversion'

/**
 * converts defined DD formats into the preferred DMS format
 * @param {String|Number} val - can be a float as plain DD or it can be a string with a provided cardinal eg. "112.12321 W"
 * @param {String} axis - latitude || longitude
 * @param {Boolean} preferWesternHemisphere - allows us to force W hemisphere for positive longitude values w/o cardinal given
 * @returns {string|null} - the formatted value or null if the value is not valid DD for formatting
 */
const convertDDtoDMSPref = (val, axis, preferWesternHemisphere = false) => {
  const r = XRegExp.exec(val, coordsDD)?.groups || null

  // regular expression does not catch
  // if prefix AND suffix are given
  if (!r) {
    return null
  }

  const v = Math.abs(parseFloat(r.degrees))
  let degrees = Math.floor(v)
  const cardinal = getCardinal(r, axis, preferWesternHemisphere)

  // if the cardinal direction could not be determined from the value,
  // err this out as an invalid value.
  // I'm not sure how you'd get to here
  if (cardinal == null) {
    return null
  }

  // LEGO-4730
  // there are values which will end up with 60 seconds because of how the rounding is working.
  // when this happens, we need to round it to 1 minute.
  // eg. "41.38333333333333 N" -> "41 22 60 N"
  let minutes = Math.floor((v - degrees) * 60)

  // minutes / 60 here is intended to be evaluated first to
  // undo the multiplication that happens above
  let seconds = Math.round((v - degrees - minutes / 60) * 3600 * 1e2) / 1e2

  if (seconds >= 60) {
    minutes += 1
    seconds -= 60
  }

  // pads degrees with a 0 if the length is 1
  degrees = degrees.toString().padStart(2, '0')

  // pads the value so it has a leading zero if > 10
  minutes = minutes.toString().padStart(2, '0')

  seconds = (
    // checks to see if the value is an int or has only one decimal place
    +seconds.toFixed(1) === seconds
      //  if so, then it just pads the left hand of the string for leading zeros if needed
      // making 2.3 -> 02.3 and 2 -> 02.0
      ? seconds.toFixed(1).padStart(4, '0')
      // if there are two decimals then we need to make sure to pad the
      // left hand side differently since there are more chars to consider.
      : seconds.toFixed(2).padStart(5, '0')
  )

  return `${degrees} ${minutes} ${seconds} ${cardinal}`
}

export default convertDDtoDMSPref
